import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, shareReplay } from 'rxjs/operators';
import { tourHolidayTypeList } from 'src/app/modules/booking-flow/holiday/holiday-search-page/helpers/holiday-types';
import fixArray from '../../../../utils/fixArray';
import { BookingService } from '../../../shared/services/booking.service';
import { HolidayPrefetchService } from '../../../shared/services/holiday-prefetch.service';
import { ModalStatusService } from '../../../shared/services/modal-status.service';
import { IDestinationBoxElement } from '../../interfaces/idestination-box-element';
import { IEngineParamChange } from '../../interfaces/iengine-param-change';
import { IGroupedAirports } from '../../interfaces/igrouped-airports';
import { EngineTabTypes } from '../../models/engine-tab-types.enum';
import { GroupAirportsPipe } from '../../pipes/group-airports.pipe';
import { IPrefetchDeparturePlacesParams } from './../../../shared/interfaces/iprefetch-departure-places-params';

@Component({
  selector: 'app-engine-holiday-modal-airport',
  templateUrl: './engine-holiday-modal-airport.component.html',
  styleUrls: ['./engine-holiday-modal-airport.component.scss']
})
export class EngineHolidayModalAirportComponent implements OnInit {

  @Input() selectedDestination: IEngineParamChange<IDestinationBoxElement>;
  @Input() selectedAccomodation: IEngineParamChange<number>;
  @Input() selectedTour: IEngineParamChange<number>;
  @Input() onlyCustomHoliday: boolean;
  @Input() onlyNetQuotes: boolean;
  @Input() defaultAirport: (number | string)[];
  @Input() isB2B: boolean;
  @Input() engineTabType: EngineTabTypes;
  @Output() selectedAirport = new EventEmitter<IEngineParamChange<(string | number)[]>>();

  loadingStatus = false;
  modal = this._activeModal;
  loadingMessage = 'Stiamo cercando le partenze per te...';
  selectedAirportInner: (number | string)[];

  airportsPrefetch$: Observable<any>;
  groupedAiports$: Observable<IGroupedAirports>;
  
  allAirportsGenericId: number = 1000108;
  allAirportsList: (number|string)[] = [];

  private maximumSelectedAirports = 3;

  constructor(private _activeModal: NgbActiveModal,
              private _modalStatusService: ModalStatusService,
              private _groupAirportsPipe: GroupAirportsPipe,
              private _bookingService: BookingService,
              private _holidayPrefetchService: HolidayPrefetchService) {
  }

  ngOnInit(): void {
    if (this.selectedDestination?.resetParams || this.selectedTour?.resetParams) {
      this.selectedAirportInner = undefined;
      this.selectedAirport.emit({value: undefined, resetParams: true});
    } else if (this.defaultAirport?.length > 0) {
      this.selectedAirportInner = this.defaultAirport;
    }
    const prefetchDepartureParams: IPrefetchDeparturePlacesParams = {
      destination: this.selectedDestination?.value?.id ? fixArray(this.selectedDestination.value.id) : undefined,
      accommodations: this.selectedAccomodation?.value ? fixArray(this.selectedAccomodation.value) : undefined,
      tours: this.selectedTour?.value ? fixArray(this.selectedTour.value) : undefined,
      onlyCustomHoliday: this.onlyCustomHoliday,
      onlyNetQuotes: this.onlyNetQuotes,
      ages: this._bookingService.getAgesArray(),
    };
    if (this.engineTabType === EngineTabTypes.Tour) {
      prefetchDepartureParams.holidayTypeList = tourHolidayTypeList;
    }
    this.loadingStatus = true;
    this.airportsPrefetch$ = this._holidayPrefetchService.prefetchDeparturePlaces(prefetchDepartureParams);
    this.groupedAiports$ = this.airportsPrefetch$.pipe(
      mergeMap(e => this. _groupAirportsPipe.transform(e)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err)),
      finalize(() => setTimeout(() => this.loadingStatus = false))
    );
    this.airportsPrefetch$.toPromise().then( allAirports => {
      // recupero i SOLI aeroporti escludendo solo soggiorno, porti e possibili altri id
      this.allAirportsList = allAirports
        ?.filter((airportId) => (airportId && airportId !== 'null' && airportId > 1000000 && airportId < 2000000))
        ?.map((airportId) => {
          if (airportId !== 'null') {
            airportId = Number(airportId);
          }
          return airportId;
        });
    });
  }

  toggleAirport(airportId: number | string, resetParams: boolean = false): void {
    this._toggleSelectedAirports(airportId);
  }


  hasSelectedAirport(airportId: number | string): boolean {
    return (this.selectedAirportInner?.indexOf(airportId) >= 0);
  }
  
  hasSelectedAllAirports(): boolean {
    return (this.selectedAirportInner?.includes(this.allAirportsGenericId));
  }

  hasSelectedOnlyLand(): boolean {
    return (this.selectedAirportInner?.includes('null'));
  }
  
  hasSelectedAirportOrHarbour(): boolean {
    return (this.selectedAirportInner?.length && !this.selectedAirportInner?.includes(this.allAirportsGenericId) && !this.selectedAirportInner?.includes('null'));
  }

  hasSelectedOnlyAirport(): boolean {
    return !!(this.selectedAirportInner?.find((selectedId) => {
      const airportIdFromAllAirportsList = this.allAirportsList
          ?.find((airportId) => airportId === selectedId);
      return airportIdFromAllAirportsList === selectedId;
    }));
  }

  confirmSelectedAirports(selectedAirportInner: (number | string)[], resetParams: boolean = false, loadNextModal : boolean = false): void {
    //  Se è stata selezionata l'opzione "Tutti gli aeroporti" (id 1000108)
    if(selectedAirportInner.includes(this.allAirportsGenericId)) {
      
      // verifico anche se sono stati selezionati dei porti in modo da concatenarli assieme a tutti gli altri aeroporti disponibili in prefetch
      const selectedDeparturesId = JSON.parse(JSON.stringify(this.allAirportsList || []));
      const selectedHarbours = selectedAirportInner.filter(selectedId => selectedId && selectedId !== 'null' && selectedId > 2000000 && selectedId < 3000000);
      if (selectedAirportInner?.length) {
        selectedDeparturesId.push(...selectedHarbours);
      }
      
      this.selectedAirport.emit({value: selectedDeparturesId, resetParams});
    }
    //  Se non è stata selezionata l'opzione "Tutti gli aeroporti"
    else {
      this.selectedAirport.emit({value: selectedAirportInner, resetParams});
    }    
    this._activeModal.dismiss();
    if(!loadNextModal) return;
    setTimeout(() => {
      const nextModalInput = document.getElementById("inputCalendarHoliday");
      if (nextModalInput) nextModalInput.click();
    }, 100);
  }

  private _toggleSelectedOnlyLand(airportId: number | string): void {
    if (
      !this.selectedAirportInner ||
      (this.selectedAirportInner?.length > 0 && this.selectedAirportInner.indexOf(airportId) < 0)
    ) {
      this.selectedAirportInner = [airportId];
    } else if (this.selectedAirportInner?.length > 0 && this.selectedAirportInner.indexOf(airportId) >= 0) {
      this.selectedAirportInner = undefined;
    }
  }

  private _toggleSelectedAirports(airportId: number | string): void {
    if (!this.selectedAirportInner) {
      this.selectedAirportInner = [];
    }
    const selectedAirportInnerIndex = this.selectedAirportInner.indexOf(airportId);
    if (selectedAirportInnerIndex >= 0) {
      this.selectedAirportInner.splice(selectedAirportInnerIndex, 1);
    } else if (this.selectedAirportInner.length < this.maximumSelectedAirports) {
      this.selectedAirportInner.push(airportId);
    }
    if (this.selectedAirportInner.length === 0) {
      this.selectedAirportInner = undefined;
    }
  }

}
